/**
 * The catalogue: `component` name → its schema and its renderer.
 *
 * The SCHEMAS are generated (`ci/gen_schemas.mjs` from `catalog.json`); this
 * table is the half that cannot be — «which React component draws this» is a
 * fact about the frontend and about nothing else. `catalog.test.ts` pins that
 * the two halves cover the same set, so a component added to the catalogue
 * fails a test here until somebody decides how it is drawn.
 *
 * A `null` renderer is a DECISION, not a gap, and there are two kinds:
 *
 *  * **structural** — `BoardSurface` and `BoardRegion` are the tree itself, laid
 *    out by `Board.tsx`, which reads their `children` directly;
 *  * **drawn elsewhere** — `ChipGroup`, `Button` and `LinkButton` ride in
 *    `region:footer` and are rendered by `Chips.tsx` in the CHAT strip, because
 *    a suggested reply is the visitor's next utterance rather than an artefact
 *    of the trip. `region` is a hint and this is the layout answering it.
 */

import type { ComponentType } from 'react';
import type { ZodTypeAny } from 'zod';

import ObjectCard from './components/ObjectCard';
import ObjectStrip from './components/ObjectStrip';
import PlanTimeline from './components/PlanTimeline';
import PlanOffer from './components/PlanOffer';
import MapCard from './components/MapCard';
import EventsList from './components/EventsList';
import WeatherCard from './components/WeatherCard';
import PriceTable from './components/PriceTable';
import NoteCard from './components/NoteCard';
import AudioPlayer from './components/AudioPlayer';
import TransportCard from './components/TransportCard';
import ScheduleCard from './components/ScheduleCard';
import SourcesCard from './components/SourcesCard';
import SkeletonCard from './components/SkeletonCard';

import { CATALOG_SCHEMAS, type CatalogComponentName } from './generated/schemas';

export type CatalogEntry = {
  schema: ZodTypeAny;
  /** `null` = validated and stored, drawn somewhere other than the board. */
  Component: ComponentType<{ props: never }> | null;
  /** Wider is better for these; the two-column board honours it above 1400px. */
  wide?: boolean;
};

/** The renderers. Anything the catalogue declares must appear here. */
const RENDERERS: Record<string, { Component: ComponentType<any> | null; wide?: boolean }> = {
  // structural — the tree, laid out by Board.tsx
  BoardSurface: { Component: null },
  BoardRegion: { Component: null },
  // drawn in the chat strip by Chips.tsx
  ChipGroup: { Component: null },
  Button: { Component: null },
  LinkButton: { Component: null },
  // the board's own cards
  ObjectCard: { Component: ObjectCard },
  ObjectStrip: { Component: ObjectStrip, wide: true },
  TripPlan: { Component: PlanTimeline, wide: true },
  // `peterhof_agent`'s offer of plans (#87) — under the answer, never on a board.
  PlanOffer: { Component: PlanOffer },
  MapCard: { Component: MapCard },
  EventsList: { Component: EventsList },
  WeatherCard: { Component: WeatherCard },
  PriceTable: { Component: PriceTable },
  NoteCard: { Component: NoteCard, wide: true },
  AudioPlayer: { Component: AudioPlayer },
  // Dormant: nothing emits it yet. Kept because `how_to_get_there` is the tool
  // behind it and a direction is part of the answer.
  TransportCard: { Component: TransportCard },
  ScheduleCard: { Component: ScheduleCard },
  SourcesCard: { Component: SourcesCard },
  SkeletonCard: { Component: SkeletonCard },
};

export const CATALOG: Record<string, CatalogEntry> = Object.fromEntries(
  Object.entries(CATALOG_SCHEMAS).map(([name, schema]) => [
    name,
    { schema, ...(RENDERERS[name] ?? { Component: null }) },
  ]),
);

export function isKnownComponent(name: string): name is CatalogComponentName {
  return Object.hasOwn(CATALOG, name);
}

export function catalogEntry(name: string): CatalogEntry | null {
  return CATALOG[name] ?? null;
}

/** Names the catalogue declares — what `catalog.test.ts` sweeps. */
export const CATALOG_NAMES = Object.keys(CATALOG_SCHEMAS).sort();
export const RENDERER_NAMES = Object.keys(RENDERERS).sort();

export type ValidationResult = { ok: true; value: unknown } | { ok: false; error: string };

/**
 * Runs a whole component node through its schema.
 *
 * The unit is the NODE, not a nested `props` object: under A2UI a component's
 * properties are flat beside its `id` and `component`, which is why the React
 * components read `props.title` unchanged from the previous contract.
 *
 * Unknown keys are stripped rather than rejected — see the generated file's
 * header for why that is the runtime rule and the drift test is the strict one.
 */
export function validateProps(component: string, node: unknown): ValidationResult {
  const found = CATALOG[component];
  if (!found) return { ok: true, value: node };
  const parsed = found.schema.safeParse(node);
  if (parsed.success) return { ok: true, value: parsed.data };
  const first = parsed.error.issues[0];
  const where = first?.path.join('.') || '(root)';
  return { ok: false, error: `${where}: ${first?.message ?? 'invalid'}` };
}
